
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { MapContainer, GeoJSON, ZoomControl } from 'react-leaflet'
import { calculateCenter } from '../../utils/LeafletUtils.js';
import 'leaflet-fullscreen/dist/Leaflet.fullscreen.js';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import Loader from './Loader';
import CountryButtonControl from './controls/CountryButtonControl';
import FixedBound from './FixedBound';
import DescriptionControl from './controls/DescriptionControl';
import MapCustomizer from './controls/MapCustomizer';
import MapCustomizerControl from './controls/MapCustomizerControl';
import MapEventHandler from './controls/MapEventHandler';
import { MapContextProvider } from '../../components/leaftletMapComponent/contexts/mapContext'
import { compareObjects } from '../../utils/LeafletUtils.js';
import { useLocation } from 'react-router';
import bjsData from "../../utils/Program_Dashboard_JsonData/Chapter&&ProgramData.json"
import ReactTable from '../common/ReactTable.jsx';
import { useTheme } from "@mui/material";
import { Marker, Popup } from "react-leaflet";
import L from "leaflet";
import mapPin from "../../images/map-pin.svg";
import { useMap } from "react-leaflet";
import Spinner from '../common/Spinner.jsx';

const normalizeMapLabel = (value) =>
    (value || "")
        .toString()
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
        .replace(/\s+/g, " ");

const DEFAULT_STATE_FILL_COLOR = "#e1e2e4";
const FIGMA_STATE_FILL_COLORS = {
    [normalizeMapLabel("Chhattisgarh")]: "#EDD34B",
    [normalizeMapLabel("Delhi")]: "#A92FDD",
    [normalizeMapLabel("Goa")]: "#DD7731",
    [normalizeMapLabel("Gujarat")]: "#E9CA8A",
    [normalizeMapLabel("Jammu and Kashmir")]: "#FC8888",
    [normalizeMapLabel("Karnataka")]: "#7598D0",
    [normalizeMapLabel("Madhya Pradesh")]: "#5BD6AA",
    [normalizeMapLabel("Maharashtra")]: "#F471A5",
    [normalizeMapLabel("Rajasthan")]: "#56BDCE",
    [normalizeMapLabel("Tamil Nadu")]: "#C6CE5E",
    [normalizeMapLabel("Uttar Pradesh")]: "#C1A6ED",
};

const getFigmaStateFillColor = (stateName) =>
    FIGMA_STATE_FILL_COLORS[normalizeMapLabel(stateName)] ||
    DEFAULT_STATE_FILL_COLOR;

function StateMap({ name, onBack }) {
    const [data, setData] = useState(null);
    const [mapCenter, setMapCenter] = useState(null);
    const [customizerActive, setCustomizerActive] = useState(false);

    const [fillOpacity, setFillOpacity] = useState(100)
    const [borderColor, setBorderColor] = useState("#000000")
    const [borderWidth, setBorderWidth] = useState(0)

    const stateFillColor = getFigmaStateFillColor(name);
    const mapStyle = useMemo(() => ({
        fillOpacity: 1,
        fillColor: stateFillColor,
        color: "#fff"
    }), [stateFillColor])

    const currentRoute = useLocation().pathname.replace("/", "");

    const [selectedDistrict, setSelectedDistrict] = useState("");
    const [selectedDistrictData, setSelectedDistrictData] = useState([]);

    const [showPopup, setShowPopup] = useState(false);

    const geoRef = useRef(null);
    const districtLayerRef = useRef(null);
    const prevLayer = useRef(null);
    const theme = useTheme()
    const districtChapterCount = {};
    bjsData.forEach(item => {
        const d = item.districtName;
        if (!districtChapterCount[d]) districtChapterCount[d] = 0;
        districtChapterCount[d] += 1;
    });

    useEffect(() => {

        const fetchData = async () => {
            const formatted = name.trim().toLowerCase().replace(/\s+/g, '-');
            const encoded = encodeURIComponent(formatted);

            const response = await fetch(
                `https://cdn.jsdelivr.net/gh/udit-001/india-maps-data@latest/geojson/states/${encoded}.geojson`
            );

            const json = await response.json();
            setData(json);
            setMapCenter(calculateCenter(json));
        };

        fetchData();
    }, [name]);


 
    useEffect(() => {
        if (selectedDistrict) {
            const filtered = bjsData.filter(
                item => item.districtName === selectedDistrict
            );
            setSelectedDistrictData(filtered);
            setShowPopup(true);
        }
    }, [selectedDistrict]);


    const highlightDistrict = (layer) => {
        if (prevLayer.current) {
            prevLayer.current.setStyle({
              //  weight: borderWidth,
                color: borderColor,
                fillColor: stateFillColor,
                fillOpacity: fillOpacity / 100
            });
        }

        layer.setStyle({
            weight: 3,
            color: "black",
            fillColor: "white",
            fillOpacity: 0.8
        });

        prevLayer.current = layer;
    };



    const onEachDistricts = (district, layer) => {
        const districtName = district?.properties?.district;
        const count = districtChapterCount[districtName] || 0;
        if (count > 0) {
            layer.bindTooltip(`${districtName} - ${count} chapter${count !== 1 ? "s" : ""}`);
        }

        if (count > 0) {
            layer.on("add", () => {
                const center = getPolygonCenter(district.geometry);

                const marker = L.marker(center, { icon: redPin });

                marker.bindPopup(`
                <b>${districtName}</b><br/>
                Chapters: ${count}
            `);

                marker.addTo(layer._map);
            });
        }

        layer.on({
            click: (e) => {
                const selected = e.target.feature.properties.district;
                setSelectedDistrict(selected);
                highlightDistrict(e.target);
            },

            mouseover: (e) => {
                e.target.setStyle({ weight: 2 });
            },

            mouseout: (e) => {
                if (prevLayer.current !== e.target) {
                    e.target.setStyle({
                        weight: borderWidth
                    });
                }
            }
        });
    };



    const getPolygonCenter = (geometry) => {
        let coords = [];

        if (geometry.type === "Polygon") {
            coords = geometry.coordinates[0];
        } else if (geometry.type === "MultiPolygon") {
            // just flatten first polygon for simplicity
            coords = geometry.coordinates[0][0];
        } else {
            return [0, 0];
        }

        let lat = 0, lng = 0;
        coords.forEach(c => {
            lng += c[0];
            lat += c[1];
        });

        return [lat / coords.length, lng / coords.length];
    };


  const redPin = new L.Icon({
  iconUrl: mapPin,
  iconSize: [22,22],      
  iconAnchor: [8, 16],     
  popupAnchor: [0, -16],   
});

    function ResizeHandler() {
        const map = useMap();

        useEffect(() => {
            setTimeout(() => {
                map.invalidateSize();
            }, 200);
        }, []);

        return null;
    }


    if (!data) return <Spinner loading={true} />;

    return (
        <div className="relative flex flex-col md:flex-row gap-4 ">

          
            <div
                style={{
                    width: "100%",
                    height: "100%",
                }}
            >

                <MapContainer
                    className="outline-none aspect-square md:aspect-4/3 lg:aspect-video"
                    zoom={6}
                    attributionControl={false}
                    center={mapCenter}
                    zoomControl={false}
                    fullscreenControl={{ position: 'bottomright' }}
                    zoomSnap={0}
                    zoomDelta={0.1}
                    dragging={false}
                    scrollWheelZoom={false}
                    doubleClickZoom={false}
                    touchZoom={false}
                    boxZoom={false}
                    keyboard={false}
                    style={{
                        width: "100%",
                        height: "100%",
                            // borderLeft: "1px solid grey",
                        backgroundColor: "transparent"
                        // backgroundColor: theme.palette.background.slate
                    }}

                >
                    <MapEventHandler />

                    <GeoJSON
                        data={data.features}
                        style={mapStyle}
                        onEachFeature={onEachDistricts}
                        ref={geoRef}
                    />


                    <CountryButtonControl position="topright" onCountryClick={onBack} />

                    <ZoomControl position='bottomright' />
                  
                </MapContainer>
            </div>

        </div>

    );
}

export default StateMap;












